import { useNavigate } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Calendar,
  Handshake,
  MoreHorizontal,
  Paperclip,
  Phone,
  Trash2,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import { formatDate, formatDistanceToNow } from "./taskFormatters";
import { PRIORITY_DOT } from "./priority";
import { formatTimeInTz, getDefaultTimezone } from "@/lib/timezones";
import type { TaskRead } from "@/api/types";

interface TaskKanbanCardProps {
  task: TaskRead;
  projectSlug: string;
  canDelete: boolean;
  isOverlay?: boolean;
  /** Soft visual de-emphasis for cards living in a done column —
   *  keeps them visible (so a freshly-completed task doesn't vanish)
   *  while making it obvious they're not in the active pipeline. */
  dimmed?: boolean;
  onOpenDetail: (task: TaskRead) => void;
  onDelete?: (task: TaskRead) => void;
}

export function TaskKanbanCard({
  task,
  projectSlug,
  canDelete,
  isOverlay = false,
  dimmed = false,
  onOpenDetail,
  onDelete,
}: TaskKanbanCardProps) {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const due = task.due_at;
  const overdue = task.is_overdue;
  const dueClass = overdue
    ? "font-semibold text-rose-600"
    : isDueToday(due)
      ? "text-amber-700"
      : "text-slate-500";

  // Calls / meetings get a coloured left rail and swap the priority
  // dot for a phone / handshake glyph so they stand out in a column
  // of plain todos at a glance.
  const isCall = task.task_type === "call";
  const isMeeting = task.task_type === "meeting";
  const isEvent = isCall || isMeeting;
  const eventStartIso = task.scheduled_at ?? (isEvent ? task.due_at : null);
  const eventStart = eventStartIso ? new Date(eventStartIso) : null;
  const eventTimeLabel = eventStart
    ? new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(eventStart)
    : null;
  // The card always shows the time in the viewer's local zone; if
  // the task was scheduled in a different zone, the tooltip surfaces
  // the original wall-clock so cross-timezone teams can spot the
  // intent without opening the detail sheet.
  const taskTz =
    task.scheduled_timezone && task.scheduled_timezone.length > 0
      ? task.scheduled_timezone
      : null;
  const viewerTz = getDefaultTimezone();
  const eventTimeTooltip =
    eventStart && taskTz && taskTz !== viewerTz
      ? `Scheduled at ${formatTimeInTz(eventStart, taskTz)}`
      : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // dnd-kit attaches the listeners to the whole node so the user
        // can grab anywhere; treat a click that didn't initiate a drag
        // as "open detail". Suppressed by the listener firing first
        // when a drag actually starts.
        if (isDragging) return;
        // Don't open the sheet if the click came from an inner control
        // (link, dropdown, button) — they handle their own intent.
        if ((e.target as HTMLElement).closest("[data-stop-drag]")) return;
        onOpenDetail(task);
      }}
      className={cn(
        "group relative flex w-full cursor-grab flex-col gap-2 rounded-xl bg-white p-3.5 text-left shadow-card-rest transition-shadow duration-150 active:cursor-grabbing motion-reduce:transition-none",
        !isDragging && "hover:shadow-plane-lift",
        isOverlay && "scale-[1.02] shadow-card-open ring-1 ring-indigo-300/40",
        dimmed && !isOverlay && "opacity-70 hover:opacity-100",
        isCall && "border-l-[3px] border-l-blue-400",
        isMeeting && "border-l-[3px] border-l-violet-400",
      )}
    >
      {/* Type glyph — phone for calls, handshake for meetings,
       *  priority dot otherwise. The icon variants take a bit more
       *  visual weight than the dot to mirror the colored left rail. */}
      {isCall ? (
        <span
          className="absolute left-3 top-3 text-blue-500"
          aria-label="Call"
        >
          <Phone className="h-3.5 w-3.5" />
        </span>
      ) : isMeeting ? (
        <span
          className="absolute left-3 top-3 text-violet-500"
          aria-label="Meeting"
        >
          <Handshake className="h-3.5 w-3.5" />
        </span>
      ) : (
        <span
          className={cn(
            "absolute left-3 top-3.5 h-2 w-2 rounded-full",
            PRIORITY_DOT[task.priority],
          )}
          aria-label={`${task.priority} priority`}
        />
      )}

      {/* Title — line-clamp 2, indented past the type glyph */}
      <div
        className={cn(
          "ml-5 pr-6 text-sm font-semibold leading-snug text-slate-900",
          dimmed && "text-slate-500 line-through decoration-slate-400",
        )}
      >
        <span className="line-clamp-2">{task.title}</span>
      </div>

      {/* Event time — bold under the title for calls / meetings so the
       *  user spots the slot at a glance. Tooltip carries the
       *  original-timezone wall-clock when the task was scheduled in
       *  a different zone than the viewer's. */}
      {eventTimeLabel && (
        <div
          className="ml-5 -mt-1 text-[12px] font-semibold tabular-nums text-slate-700"
          title={eventTimeTooltip ?? undefined}
        >
          {eventTimeLabel}
        </div>
      )}

      {/* Contact name — events that target a specific person show
       *  who, not just which company. */}
      {isEvent && task.contact_name && (
        <div className="ml-5 -mt-1 truncate text-[11px] text-slate-500">
          with {task.contact_name}
        </div>
      )}

      {/* Company link — only if linked */}
      {task.company_id && task.company_name && (
        <div data-stop-drag className="ml-5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${projectSlug}/companies/${task.company_id}`);
            }}
            className="truncate text-xs text-indigo-600 hover:text-indigo-500 hover:underline"
          >
            {task.company_name}
          </button>
        </div>
      )}

      {/* Footer — assignee + due date. The due chip is hidden on
       *  events because the prominent time line above already tells
       *  the user when this happens. */}
      <div className="ml-5 flex items-center justify-between gap-2 pt-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar className="h-5 w-5 border border-slate-200">
            <AvatarFallback className="bg-slate-900 text-[9px] font-semibold text-white">
              {initials(task.assigned_to_name)}
            </AvatarFallback>
          </Avatar>
          <span className="max-w-[110px] truncate text-xs text-slate-600">
            {task.assigned_to_name}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Informational only — no popover, no onClick. The
           *  card's own click handler opens the sheet, which shows
           *  the full attachment list. Hidden when the count is 0
           *  or unknown so the footer stays uncluttered. */}
          {typeof task.attachments_count === "number" &&
            task.attachments_count > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] text-slate-500"
                aria-label={`${task.attachments_count} attachment${
                  task.attachments_count === 1 ? "" : "s"
                }`}
              >
                <Paperclip className="h-3 w-3" />
                <span className="tabular-nums">
                  {task.attachments_count}
                </span>
              </span>
            )}
          {due && !isEvent && (
            <span
              className={cn(
                "inline-flex items-center gap-1 text-[11px]",
                dueClass,
              )}
            >
              <Calendar className="h-3 w-3" />
              {overdue
                ? formatDistanceToNow(new Date(due))
                : formatDate(new Date(due))}
            </span>
          )}
        </div>
      </div>

      {/* Hover actions — top-right ⋯
       *
       *  Why every interactive piece in here calls stopPropagation:
       *  Radix renders DropdownMenuContent in a Portal, which detaches
       *  it from the card's DOM subtree — but React still bubbles
       *  synthetic click events through the React component tree. The
       *  card's outer onClick uses `closest("[data-stop-drag]")` to
       *  filter clicks, and `closest` walks the *DOM* tree, so a click
       *  on a portaled item never matches the data-stop-drag wrapper
       *  and the detail sheet would open underneath the menu. Calling
       *  stopPropagation on every item + the trigger keeps the React
       *  synthetic event from ever reaching the card. */}
      {(canDelete || onDelete) && (
        <div
          data-stop-drag
          className="absolute right-1 top-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Task actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={(e) => e.stopPropagation()}
                onSelect={() => onOpenDetail(task)}
              >
                View details
              </DropdownMenuItem>
              {canDelete && onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => e.stopPropagation()}
                    onSelect={() => onDelete(task)}
                    className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

function isDueToday(iso: string | null): boolean {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  } catch {
    return false;
  }
}
